import type { IDropdownButtonProps } from "@similarweb/ui-components";
import type { IEllipsisDropdownItemProps } from "@similarweb/ui-components";
import { translate } from "app/filters/i18nFilter";
import { isEmpty } from "lodash";
import { searchDropdownItems } from "app/pages/sales-intelligence/utils/dropdowns";
import type { Topic } from "app/pages/sales-intelligence/sub-modules/intent/types/common";
import { useCallback } from "react";

import useSignalFilterDropdownLabel from "../../../hooks/useSignalFilterDropdownLabel";
import SignalFilterDropdownLabel from "../shared/SignalFilterDropdownLabel/SignalFilterDropdownLabel";
import useMultiselectHandler from "../../../hooks/useMultiselectHandler";
import { FilterType } from "../../../models/services/FilterService";
import filterService from "../../../services/filters/filterService";
import FilterButtonLabel from "../../filters/shared/FilterDropdown/FilterButtonLabel/FilterButtonLabel";
import FilterSelectAllItem from "../../filters/shared/FilterDropdown/FilterSelectAllItem/FilterSelectAllItem";
import SignalFilterDropdownButton from "app/pages/sales-intelligence/sub-modules/signals/components/filters/shared/FilterDropdown/SignalFilterDropdownButton/SignalFilterDropdownButton";
import FilterMultiselectDropdown from "../shared/FilterMultiselectDropdown/FilterMultiselectDropdown";
import useUncontrolledDropdown from "app/pages/sales-intelligence/hooks/useUncontrolledDropdown";
import useDelayedAction from "app/pages/sales-intelligence/hooks/useDelayedAction";
import { DROPDOWN_AUTO_OPEN_DELAY } from "app/pages/sales-intelligence/sub-modules/signals/constants/dropdown";
import FilterDropdownItemWithCheckboxAndWrapping from "app/pages/sales-intelligence/sub-modules/signals/components/filters/shared/FilterDropdown/FilterDropdownItemwithCheckboxAndWrapping/FilterDropdownItemwithCheckboxAndWrapping";

type DropdownButtonProps = Pick<IDropdownButtonProps, "isOpen" | "disabled">;

type FilterValue = ReadonlyArray<Topic["name"]> | undefined;

export type SignalIntentTopicsFilterProps = {
    filterKey: string;
    selectedValue: FilterValue;
    options: ReadonlyArray<Readonly<Topic>>;
    onValueChange(filterValue: FilterValue): void;
    autoOpen?: boolean;
};

const SignalIntentTopicsFilter = ({
    filterKey,
    selectedValue,
    options,
    onValueChange,
    autoOpen,
}: SignalIntentTopicsFilterProps) => {
    const isFullSelection = options.length === selectedValue?.length;

    const { dropdownRef, onUncontrolledOpen } = useUncontrolledDropdown();

    useDelayedAction(DROPDOWN_AUTO_OPEN_DELAY, onUncontrolledOpen, autoOpen);

    const buildFilterName = useCallback(() => {
        const nameBuilder = filterService.getFilterName(FilterType.IntentTopics);

        return nameBuilder.buildName({ topics: selectedValue ?? [] }, options);
    }, [options, selectedValue]);

    const { model: filterLabelModel } = useSignalFilterDropdownLabel({ buildName: buildFilterName });

    const { onSelect } = useMultiselectHandler({
        selectedValue,
        onValueChange,
    });

    const renderButton = (props: DropdownButtonProps) => {
        const isEmptySelection = isEmpty(selectedValue);
        return (
            <SignalFilterDropdownButton
                {...props}
                id={filterKey}
                hasValue={!isEmptySelection}
            >
                <FilterButtonLabel>
                    <SignalFilterDropdownLabel model={filterLabelModel} />
                </FilterButtonLabel>
            </SignalFilterDropdownButton>
        );
    };

    const renderSelectAllItem = (props: IEllipsisDropdownItemProps) => {
        return (
            <FilterSelectAllItem {...props}>
                {translate(`si.signals.filters.${filterKey}.name.all`, { count: options.length })}
            </FilterSelectAllItem>
        );
    };

    const renderItem = (item: Readonly<Topic>) => (
        <FilterDropdownItemWithCheckboxAndWrapping
            key={item.name}
            id={item.name}
        >
            {item.name}
        </FilterDropdownItemWithCheckboxAndWrapping>
    );

    const handleChange = useCallback(
        (itemId: string | null, isSelected: boolean) => {
            if (itemId === null) {
                const changedValues = isSelected ? [] : options.map(({ name }) => name);

                onValueChange(changedValues);
            } else {
                onSelect(itemId, isSelected);
            }
        },
        [onSelect, onValueChange, options],
    );

    return (
        <FilterMultiselectDropdown
            ref={dropdownRef}
            filterKey={filterKey}
            selectedOptions={selectedValue ?? []}
            isFullSelection={isFullSelection}
            searchFilter={searchDropdownItems}
            buttonComponent={renderButton}
            selectAllComponent={renderSelectAllItem}
            processOptions
            hasSearch
            onChange={handleChange}
        >
            {options.map(renderItem)}
        </FilterMultiselectDropdown>
    );
};

export default SignalIntentTopicsFilter;
